import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { apiClient, formatApiError } from '../lib/api';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const signupPlans = [
  { nameKey: 'starter', regularPrice: '₹399', price: '₹249', detailKey: 'hundredInvoicesMonth' },
];

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const { user, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const loc = useLocation();
  const redirect = params.get('redirect') || loc.state?.from || '/';
  const googleButtonRef = useRef(null);

  useEffect(() => {
    if (user && user !== false) navigate(redirect, { replace: true });
  }, [user, navigate, redirect]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/auth/config');
        if (!cancelled && data?.google?.client_id) setGoogleClientId(data.google.client_id);
      } catch {
        // Hide Google sign-in when OAuth is not configured.
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) return;
    let cancelled = false;
    const mountGoogle = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) return;
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          if (!credential) return;
          setLoading(true);
          try {
            await loginWithGoogle(credential);
            setWelcome(true);
            toast.success(t('welcome'));
            window.setTimeout(() => navigate(redirect), 650);
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: googleButtonRef.current.offsetWidth || 320,
        text: 'continue_with',
        shape: 'rectangular',
      });
    };
    if (window.google?.accounts?.id) {
      mountGoogle();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = mountGoogle;
      document.body.appendChild(script);
    }
    return () => { cancelled = true; };
  }, [googleClientId, loginWithGoogle, navigate, redirect]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left — OAuth */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg leading-none">b</span>
            </div>
            <span className="font-heading font-black text-slate-900 text-lg">billjaldi</span>
          </Link>

          <h1 className="font-heading text-3xl font-black tracking-tight mb-2">{t('continueWithGoogle')}</h1>

          {welcome ? (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800 shadow-sm" data-testid="login-welcome-state">
              <ShieldCheck className="w-4 h-4 inline mr-2" />
              {t('openingWorkspace')}
            </div>
          ) : googleClientId ? (
            <div className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${loading ? 'pointer-events-none opacity-60' : ''}`}>
              {loading && (
                <div className="mb-3 flex items-center justify-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('verifyingGoogle')}
                </div>
              )}
              <div ref={googleButtonRef} className="w-full min-h-10 flex items-center justify-center" data-testid="google-login-button" />
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-500 shadow-sm" data-testid="google-login-loading">
              {configLoaded ? t('googleUnavailable') : t('loadingGoogle')}
            </div>
          )}

          <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-orange-600 font-black mb-2">{t('starter')}</div>
            <div className="flex items-end justify-between gap-4">
              <div className="text-sm text-slate-600">{t('starterDetail')}</div>
              <div className="text-right">
                <div className="font-heading text-2xl font-black text-slate-900">₹249</div>
                <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-black text-slate-600">
                  <span>{t('regularPrice')}</span>
                  <span className="line-through decoration-2 decoration-orange-500">₹399</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right — brand visual */}
      <div className="hidden lg:flex items-center justify-center bg-slate-900 text-white p-16 relative overflow-hidden grain-overlay" data-testid="auth-brand-side">
        <div className="relative max-w-md">
          <ShieldCheck className="w-10 h-10 text-orange-400 mb-6" />
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-orange-300 mb-3">{t('welcome')}</div>
          <div className="font-heading text-4xl font-black leading-tight mb-4">
            {t('loginRightHeadline')}
          </div>
          <p className="text-slate-300 leading-relaxed">
            {t('loginRightCopy')}
          </p>
          <div className="mt-8 space-y-3">
            {signupPlans.map((p) => (
              <div key={p.nameKey} className="flex items-center justify-between gap-4 border border-white/10 rounded-lg px-4 py-3 bg-white/[0.03]">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{t(p.nameKey)}</div>
                  <div className="text-sm text-slate-300">{t(p.detailKey)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-black uppercase tracking-wider text-orange-300">{t('launchPrice')}</div>
                  <div className="font-heading text-2xl font-black">{p.price}</div>
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[11px] font-black text-slate-300">
                    <span>{t('regularPrice')}</span>
                    <span className="line-through decoration-2 decoration-orange-300">{p.regularPrice}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
